import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
    Box, Paper, Stack, Typography, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, Button, TextField, MenuItem, Pagination
} from "@mui/material";
import { Visibility, Refresh } from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";

/* ===== Utils ===== */
const toYMD = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === "string") {
        const ymd = v.split("T")[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
    }
    return null;
};
const formatDDMMYYYY = (v) => {
    const ymd = toYMD(v);
    if (!ymd) return "";
    const [y, m, d] = ymd.split("-");
    return `${d}-${m}-${y}`;
};
const normNoticia = (n = {}) => ({
    id: n.id,
    nombre: n.nombre ?? "",
    descripcion: n.descripcion ?? "",
    fechaFin: toYMD(n.fechaFin ?? n.fecha_fin ?? null),
});

/* HTML clamp a N líneas con detección de truncado */
const ClampedHTML = ({ html, lines = 3, onClampedChange }) => {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const check = () => {
            const clamped = el.scrollHeight > el.clientHeight + 1;
            onClampedChange?.(clamped);
        };
        const t = setTimeout(check, 0);
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => { clearTimeout(t); ro.disconnect(); };
    }, [html, lines, onClampedChange]);

    return (
        <Box
            ref={ref}
            sx={{
                display: "-webkit-box",
                WebkitLineClamp: lines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                "& p": { m: 0 }
            }}
            dangerouslySetInnerHTML={{ __html: html || "" }}
        />
    );
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const Noticias = ({ defaultPageSize = 10 }) => {
    const API = process.env.REACT_APP_API_URL || "http://localhost:8080";
    const { token: ctxToken } = useAuth();
    const token = ctxToken || localStorage.getItem("token");

    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [page, setPage] = useState(0);               // 0-based (MUI Pagination es 1-based)
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [loading, setLoading] = useState(false);

    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerRow, setViewerRow] = useState(null);

    const [clampedMap, setClampedMap] = useState({});  // id -> bool

    const totalPages = Math.max(1, Math.ceil((rowCount || 0) / pageSize));

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, size: pageSize, sort: "fechaFin,desc" }; // ← ¡campo de entidad!
            const { data } = await axios.get(`${API}/api/noticias/vigentes`, { params, headers });
            const content = Array.isArray(data?.content) ? data.content : [];
            setRows(content.map(normNoticia));
            setRowCount(Number.isFinite(data?.totalElements) ? data.totalElements : content.length);
        } catch (e) {
            console.error("Error cargando noticias:", e);
        } finally {
            setLoading(false);
        }
    }, [API, headers, page, pageSize]);

    useEffect(() => { fetchData(); }, [fetchData]);

    return (
        <Paper
            elevation={6}
            sx={{
                p: { xs: 1.5, md: 2 },
                borderRadius: 4,
                background: "#f9fbff",
                width: "100%",
            }}
        >
            {/* Toolbar: tamaño + refrescar (derecha) */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
            >
                {/* Izquierda */}
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Tablón de Noticias
                </Typography>

                {/* Derecha */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        label="Por página"
                        size="small"
                        select
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                        sx={{ width: 120 }}
                    >
                        {PAGE_SIZE_OPTIONS.map(n => (
                            <MenuItem key={n} value={n}>{n}</MenuItem>
                        ))}
                    </TextField>
                    <IconButton onClick={fetchData} disabled={loading}>
                        <Refresh />
                    </IconButton>
                </Stack>
            </Stack>


            {/* Lista vertical */}
            <Stack spacing={1.25}>
                {rows.map((n) => (
                    <Paper key={n.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {n.nombre}
                        </Typography>

                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <Box sx={{ flex: 1 }}>
                                <ClampedHTML
                                    html={n.descripcion}
                                    lines={3}
                                    onClampedChange={(isClamped) =>
                                        setClampedMap((m) => (m[n.id] === isClamped ? m : { ...m, [n.id]: isClamped }))
                                    }
                                />
                            </Box>
                            {clampedMap[n.id] && (
                                <IconButton
                                    size="small"
                                    aria-label="Ver completa"
                                    onClick={() => { setViewerRow(n); setViewerOpen(true); }}
                                    sx={{ mt: 0.5 }}
                                    title="Ver completa"
                                >
                                    <Visibility fontSize="small" />
                                </IconButton>
                            )}
                        </Stack>

                        {n.fechaFin && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                Hasta {formatDDMMYYYY(n.fechaFin)}
                            </Typography>
                        )}
                    </Paper>
                ))}

                {rows.length === 0 && !loading && (
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                        No hay noticias vigentes.
                    </Typography>
                )}
            </Stack>

            {/* Paginación (1-based visual) */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Página {page + 1} de {totalPages} · {rowCount ?? 0} noticias
                </Typography>
                <Pagination
                    count={totalPages}
                    page={page + 1}
                    onChange={(_, val) => setPage(val - 1)}
                    color="primary"
                    shape="rounded"
                    size="small"
                    siblingCount={0}
                    boundaryCount={1}
                />
            </Stack>

            {/* Visor completo */}
            <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{viewerRow?.nombre ?? "Noticia"}</DialogTitle>
                <DialogContent dividers>
                    {viewerRow?.descripcion ? (
                        <div dangerouslySetInnerHTML={{ __html: viewerRow.descripcion }} />
                    ) : (
                        <Typography color="text.secondary">Sin descripción</Typography>
                    )}
                    {viewerRow?.fechaFin && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Hasta {formatDDMMYYYY(viewerRow.fechaFin)}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewerOpen(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default Noticias;
